import { useControlledState } from '@react-stately/utils';
import { createContext, useMemo, useRef, useState } from 'react';

/**
 * Represents the current state of autocomplete.
 */
type AutoCompleteState = {
    /**
     * Value used to filter results.
     */
    filterValue: string;
    /**
     * Value of the input.
     */
    value: string;
    /**
     * Set the value of the input.
     */
    setValue: (value: string) => void;
    /**
     * The ref of the element used as input.
     */
    inputRef: React.RefObject<HTMLElement>;
    /**
     * True when the user has started to type something.
     */
    isTouched: boolean;
};

/**
 * Autocomplete state context.
 */
export const AutoCompleteStateContext = createContext<AutoCompleteState | null>(null);

export type AutoCompleteProps = {
    children: React.ReactNode;
    /**
     * The ref of the element used as input.
     */
    inputRef?: React.RefObject<HTMLElement>;
    /**
     * Default value set in input.
     */
    defaultValue?: string | undefined;
    /**
     * Value used for filtering.
     * By default it's the same as the value.
     */
    filterValue?: string | undefined;
    /**
     * Controlled value of the input.
     */
    value?: string | undefined;
    /**
     * Callback when the value changes.
     */
    onValueChange?: (value: string) => void;
};

/**
 * Autocomplete wrapper that injects the autocomplete state.
 */
export function AutoComplete(props: AutoCompleteProps) {
    const {
        children,
        defaultValue = '',
        filterValue: filterValueProp,
        value: valueProp,
        onValueChange,
        inputRef: inputRefProp,
    } = props;
    const defaultInputRef = useRef<HTMLInputElement>(null);
    const inputRef = inputRefProp ?? defaultInputRef;
    const [value, setValue] = useControlledState(valueProp, defaultValue, onValueChange);
    const filterValue = filterValueProp ?? value;
    const [isTouched, setIsTouched] = useState(Boolean(value));
    const state = useMemo<AutoCompleteState>(
        () => ({
            inputRef,
            filterValue,
            value,
            isTouched,
            setValue: (value: string) => {
                setValue(value);
                setIsTouched(true);
            },
        }),
        [value, filterValue, isTouched, setValue, inputRef]
    );
    return (
        <AutoCompleteStateContext.Provider value={state}>
            {children}
        </AutoCompleteStateContext.Provider>
    );
}
